"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import { 
  X, 
  ArrowLeft, 
  ArrowRight, 
  Play, 
  Grid2X2, 
  Video,
  SlidersHorizontal
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";

interface PropertyImage {
  id: number;
  url: string;
  alt: string;
  isVideo?: boolean;
}

interface PropertyGalleryClientProps {
  images: PropertyImage[];
}

/**
 * PropertyGalleryClient - Client Component for interactive gallery functionality
 * 
 * Safe Migration Phase 3: Enhanced with Next.js Image optimization
 * - All lightbox functionality preserved
 * - Carousel and grid view modes maintained
 * - Responsive mobile behavior identical
 * - All animations and interactions preserved
 * - Added Next.js Image optimization for better performance
 * - Lazy loading for improved page speed
 */
export function PropertyGalleryClient({ images }: PropertyGalleryClientProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [viewMode, setViewMode] = useState<'carousel' | 'grid'>('grid');
  const [isMobile, setIsMobile] = useState(false);
  const mainImage = images[0];
  const restImages = images.slice(1, 5); // Show only 4 thumbnails initially
  const hiddenImagesCount = images.length > 5 ? images.length - 5 : 0;

  // Detect mobile viewport on component mount and window resize
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768); // Consider mobile if width is less than 768px (md breakpoint)
    };

    // Check initially
    checkIfMobile();
    
    // Set default view mode based on device
    setViewMode(window.innerWidth < 768 ? 'carousel' : 'grid');

    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup event listener
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const toggleViewMode = () => {
    setViewMode(prev => prev === 'carousel' ? 'grid' : 'carousel');
  };

  return (
    <>
      {/* Gallery section */}
      <div className="relative bg-[var(--color-pure-white)] rounded-xl shadow-md overflow-hidden animate-fade-in">
        {/* Gallery view mode toggle */}
        <div className="absolute top-4 right-4 z-10 flex gap-2">
          <Button 
            variant="ghost" 
            size="icon" 
            className={`rounded-full ${viewMode === 'carousel' ? 'bg-[var(--color-pure-white)]/80' : 'bg-black/20 hover:bg-black/30'}`}
            onClick={toggleViewMode}
          >
            <SlidersHorizontal className="text-[var(--color-brand-primary)]" size={18} />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className={`rounded-full ${viewMode === 'grid' ? 'bg-[var(--color-pure-white)]/80' : 'bg-black/20 hover:bg-black/30'}`}
            onClick={toggleViewMode}
          >
            <Grid2X2 className="text-[var(--color-brand-primary)]" size={18} />
          </Button>
        </div>

        {viewMode === 'carousel' ? (
          <div className="relative">
            {/* Main image carousel */}
            <Carousel className="w-full">
              <CarouselContent>
                {images.map((img, i) => (
                  <CarouselItem key={img.id}>
                                         <div 
                       className="relative aspect-square cursor-pointer"
                       onClick={() => openLightbox(i)}
                     >
                       <Image
                         fill
                         src={img.url} 
                         alt={img.alt} 
                         className="object-cover"
                         sizes="(max-width: 768px) 100vw, 50vw"
                         priority={i === 0}
                         quality={85}
                       />
                      {img.isVideo && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-16 h-16 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
                            <Play fill="white" size={30} className="text-white ml-1" />
                          </div>
                        </div>
                      )}
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>

              <CarouselPrevious className="left-2 bg-[var(--color-pure-white)]/80 hover:bg-[var(--color-pure-white)]/90" />
              <CarouselNext className="right-2 bg-[var(--color-pure-white)]/80 hover:bg-[var(--color-pure-white)]/90" />
            </Carousel>

            {/* Thumbnail strip */}
            <div className="flex gap-2 p-4 overflow-x-auto">
              {images.map((img, idx) => (
                <div 
                  key={img.id} 
                  className={`relative shrink-0 w-24 h-16 rounded overflow-hidden cursor-pointer border-2 transition-all
                    ${idx === currentImageIndex ? 'border-[var(--color-brand-primary)] shadow-md' : 'border-transparent'}`}
                  onClick={() => openLightbox(idx)}
                >
                  <Image
                    fill
                    src={img.url} 
                    alt={img.alt} 
                    className="object-cover"
                    sizes="96px"
                    quality={70}
                  />
                  {img.isVideo && (
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                      <Video size={16} className="text-white" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-1">
            {/* Main large image */}
            <div 
              className="relative aspect-square col-span-1 row-span-1 cursor-pointer"
              onClick={() => openLightbox(0)}
            >
              <Image
                fill
                src={mainImage.url} 
                alt={mainImage.alt} 
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 50vw"
                priority={true}
                quality={85}
              />
              {mainImage.isVideo && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
                    <Play fill="white" size={30} className="text-white ml-1" />
                  </div>
                </div>
              )}
            </div>

            {/* Grid of smaller images */}
            <div className="grid grid-cols-2 gap-1">
              {restImages.map((img, idx) => (
                <div 
                  key={img.id}
                  className="relative aspect-square cursor-pointer"
                  onClick={() => openLightbox(idx + 1)}
                >
                  <Image
                    fill
                    src={img.url} 
                    alt={img.alt} 
                    className="object-cover"
                    sizes="(max-width: 768px) 50vw, 25vw"
                    priority={idx < 2}
                    quality={80}
                  />
                  {img.isVideo && (
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                      <Video size={16} className="text-white" />
                    </div>
                  )}
                  {idx === restImages.length - 1 && hiddenImagesCount > 0 && (
                    <div 
                      className="absolute inset-0 bg-black/40 flex items-center justify-center cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        openLightbox(5);
                      }}
                    >
                      <span className="text-white font-medium text-lg">+{hiddenImagesCount}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* View all photos button */}
            <Button 
              variant="secondary"
              className="absolute bottom-4 right-4 bg-[var(--color-pure-white)]/90 hover:bg-[var(--color-pure-white)] text-[var(--color-brand-primary)] flex gap-2 rounded-full"
              onClick={() => openLightbox(0)}
            >
              <Grid2X2 size={16} />
              <span>View All Photos</span>
            </Button>
          </div>
        )}
      </div>

      {/* Lightbox - using simple CSS transitions instead of framer-motion */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex flex-col items-center justify-center p-4 md:p-10 transition-opacity duration-300 ease-in-out"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <Button 
            variant="ghost" 
            size="icon" 
            className="absolute top-4 right-4 bg-black/50 hover:bg-black/80 text-white rounded-full z-10"
            onClick={closeLightbox}
          >
            <X size={24} />
          </Button>
          
          {/* Navigation buttons */}
          <Button 
            variant="ghost" 
            size="icon" 
            className="absolute left-4 md:left-10 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/80 text-white rounded-full z-10"
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
          >
            <ArrowLeft size={24} />
          </Button>
          
          <Button 
            variant="ghost" 
            size="icon" 
            className="absolute right-4 md:right-10 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/80 text-white rounded-full z-10"
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
          >
            <ArrowRight size={24} />
          </Button>
          
          {/* Image */}
          <div
            key={currentImageIndex}
            className="relative max-h-[80vh] max-w-[90vw] overflow-hidden flex items-center justify-center transition-all duration-300 ease-in-out"
            onClick={(e) => e.stopPropagation()}
          >
            {images[currentImageIndex].isVideo ? (
              <video 
                controls
                className="max-h-[80vh] max-w-[90vw]"
              >
                <source src={images[currentImageIndex].url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <img 
                src={images[currentImageIndex].url}
                alt={images[currentImageIndex].alt}
                className="max-h-[80vh] max-w-[90vw] object-contain"
              />
            )}
          </div>
          
          {/* Image counter */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/60 px-4 py-2 rounded-full text-white text-sm">
            {currentImageIndex + 1} / {images.length}
          </div>
          
          {/* Thumbnails */}
          <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2 overflow-x-auto max-w-[90vw] p-2">
            {images.map((img, idx) => (
              <div 
                key={img.id}
                className={`w-16 h-12 shrink-0 rounded overflow-hidden border-2 transition-all cursor-pointer ${
                  idx === currentImageIndex ? 'border-[var(--color-brand-primary)] shadow-md' : 'border-transparent opacity-60 hover:opacity-100'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex(idx);
                }}
              >
                <Image
                  fill
                  src={img.url} 
                  alt={`Thumbnail ${idx+1}`}
                  className="object-cover"
                  sizes="64px"
                  quality={65}
                />
                {img.isVideo && (
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                    <Video size={14} className="text-white" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
} 